import React, { useMemo, useState, useEffect, useRef } from "react"
import DynamicDateSelector from "./DynamicDateSelector"
import EnhancedVariableReference from "./EnhancedVariableReference"
import {
  DATE_EXPRESSION_MODES,
  DATE_OFFSET_UNITS,
  inferDateExpressionMode,
  extractDynamicDateToken,
  dynamicOptionToToken,
  parseDynamicTokenInner,
  normalizeDateOffset,
} from "@/lib/formDateFieldConfig"
import { DEFAULT_DYNAMIC_DATE_FORMAT } from "@/lib/dateUtils"

/**
 * Editor for a date expression used as default / min / max on form date fields.
 *
 * Modes: none | static (YYYY-MM-DD) | dynamic (~token~) | variable ({{var}} + optional offset)
 */
export default function FormDateExpressionEditor({
  label,
  value = "",
  offset = null,
  onChange,
  onBlur,
  availableVariables = [],
  dateFormat = DEFAULT_DYNAMIC_DATE_FORMAT,
  className = "",
}) {
  const inferredMode = useMemo(
    () => inferDateExpressionMode(value, offset),
    [value, offset],
  )
  const [mode, setMode] = useState(inferredMode)
  // When the user picks a mode, keep it even if we clear the value for that mode
  // (empty values otherwise infer as "none" and snap the select back).
  const modeLockRef = useRef(null)

  const tokenInner = extractDynamicDateToken(value)
  const parsedDynamic = useMemo(
    () => parseDynamicTokenInner(tokenInner || "today"),
    [tokenInner],
  )

  const [dynamicOption, setDynamicOption] = useState(parsedDynamic.option)
  const [customDays, setCustomDays] = useState(parsedDynamic.customDays)
  const [customPeriod, setCustomPeriod] = useState(parsedDynamic.customPeriod)
  const [showCustomInput, setShowCustomInput] = useState(
    parsedDynamic.showCustomInput,
  )

  const normalizedOffset = normalizeDateOffset(offset) || {
    amount: 0,
    unit: "days",
  }

  useEffect(() => {
    const inferred = inferDateExpressionMode(value, offset)
    const locked = modeLockRef.current

    if (locked) {
      // Stay on the user-chosen mode while the value is empty / still matching that mode.
      if (inferred === "none" || inferred === locked) {
        setMode(locked)
        return
      }
      // Value now clearly belongs to a different mode (e.g. pasted a ~token~).
      modeLockRef.current = null
    }

    setMode(inferred)
  }, [value, offset])

  useEffect(() => {
    if (mode !== "dynamic") return
    const parsed = parseDynamicTokenInner(
      extractDynamicDateToken(value) || "today",
    )
    setDynamicOption(parsed.option)
    setCustomDays(parsed.customDays)
    setCustomPeriod(parsed.customPeriod)
    setShowCustomInput(parsed.showCustomInput)
  }, [mode, value])

  const emit = (nextValue, nextOffset = null) => {
    onChange?.({
      value: nextValue == null ? "" : String(nextValue),
      offset: normalizeDateOffset(nextOffset),
    })
  }

  const handleModeChange = (nextMode) => {
    modeLockRef.current = nextMode
    setMode(nextMode)
    if (nextMode === "none") {
      emit("", null)
      return
    }
    if (nextMode === "static") {
      const keep = /^\d{4}-\d{2}-\d{2}$/.test(String(value || "").trim())
        ? String(value).trim()
        : ""
      emit(keep, null)
      return
    }
    if (nextMode === "dynamic") {
      const token = value?.includes("~")
        ? value
        : dynamicOptionToToken("today")
      emit(token, null)
      setDynamicOption(extractDynamicDateToken(token) || "today")
      setShowCustomInput(false)
      return
    }
    if (nextMode === "variable") {
      const keep =
        String(value || "").includes("{{") || String(value || "").includes("{")
          ? value
          : ""
      emit(keep, normalizeDateOffset(offset))
    }
  }

  const handleDynamicOption = (option) => {
    setDynamicOption(option)
    if (option === "custom") {
      setShowCustomInput(true)
      return
    }
    setShowCustomInput(false)
    emit(dynamicOptionToToken(option), null)
    onBlur?.()
  }

  const handleCustomPeriodApply = () => {
    emit(dynamicOptionToToken("custom", customDays, customPeriod), null)
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {label ? (
        <label className="dark:text-white text-sm font-medium">{label}</label>
      ) : null}

      <select
        value={mode}
        onChange={(e) => {
          handleModeChange(e.target.value)
          onBlur?.()
        }}
        className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 nodrag"
      >
        {DATE_EXPRESSION_MODES.map((m) => (
          <option key={m.value} value={m.value}>
            {m.label}
          </option>
        ))}
      </select>

      {mode === "static" && (
        <input
          type="date"
          value={
            /^\d{4}-\d{2}-\d{2}$/.test(String(value || "").trim())
              ? String(value).trim()
              : ""
          }
          onChange={(e) => emit(e.target.value, null)}
          onBlur={() => onBlur?.()}
          className="w-full cursor-pointer px-2 py-1.5 rounded border border-gray-300 text-sm bg-white text-gray-900 dark:bg-[#1f2937] dark:text-white dark:border-gray-600 nodrag"
        />
      )}

      {mode === "dynamic" && (
        <div
          className="nodrag"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <DynamicDateSelector
            value={dynamicOption}
            onChange={handleDynamicOption}
            onCustomPeriodChange={() => {
              handleCustomPeriodApply()
              onBlur?.()
            }}
            customDays={customDays}
            setCustomDays={setCustomDays}
            customPeriod={customPeriod}
            setCustomPeriod={setCustomPeriod}
            showCustomInput={showCustomInput}
            setShowCustomInput={setShowCustomInput}
          />
        </div>
      )}

      {mode === "variable" && (
        <div className="space-y-2">
          <EnhancedVariableReference
            value={value || ""}
            onChange={(v) =>
              emit(
                v,
                normalizeDateOffset(offset) ||
                  (normalizedOffset.amount
                    ? {
                        amount: normalizedOffset.amount,
                        unit: normalizedOffset.unit || "days",
                      }
                    : null),
              )
            }
            onBlur={() => onBlur?.()}
            availableVariables={availableVariables}
            placeholder="{{date_variable}} or ~today~..."
            className="text-sm bg-white text-gray-900 border-gray-300 dark:bg-[#1f2937] dark:text-white dark:border-gray-600"
            showDateVariables={true}
            showSystemVariables={false}
            showPreview={true}
            dateFormat={dateFormat}
          />
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
              Offset
            </label>
            <input
              type="number"
              min={0}
              value={normalizedOffset.amount || ""}
              placeholder="0"
              onChange={(e) => {
                const raw = e.target.value
                if (raw === "" || raw === "0") {
                  emit(value, null)
                  return
                }
                const amount = parseInt(raw, 10)
                if (!Number.isFinite(amount) || amount <= 0) {
                  emit(value, null)
                  return
                }
                emit(value, {
                  amount,
                  unit: normalizedOffset.unit || "days",
                })
              }}
              onBlur={() => onBlur?.()}
              className="w-20 px-2 py-1 rounded border border-gray-300 text-sm dark:bg-[#1f2937] dark:text-white dark:border-gray-600 nodrag"
            />
            <select
              value={normalizedOffset.unit || "days"}
              onChange={(e) => {
                const unit = e.target.value
                if (!normalizedOffset.amount) {
                  emit(value, null)
                  return
                }
                emit(value, { amount: normalizedOffset.amount, unit })
                onBlur?.()
              }}
              className="flex-1 px-2 py-1 rounded border border-gray-300 text-sm dark:bg-[#1f2937] dark:text-white dark:border-gray-600 nodrag"
            >
              {DATE_OFFSET_UNITS.map((u) => (
                <option key={u.value} value={u.value}>
                  {u.label}
                </option>
              ))}
            </select>
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
            Optional offset applied after the variable (or date token) resolves,
            e.g. 3 days from an extracted date.
          </p>
        </div>
      )}
    </div>
  )
}
